/**
 * @file        TelegramAuthBlock.tsx
 * @description Bot deep-link auth gate (replaced Telegram Login Widget on 2026-05-10).
 *
 *              Renders progressive UI based on (usedCount, maxCount, isLimitReached):
 *              - 0 used → renders null (no UI)
 *              - usedCount = maxCount-1 → amber teaser banner
 *              - isLimitReached → blue gate with bot deep-link button
 *
 * @dependencies /api/auth/bot/initiate, CONTACTS, trackGoal
 * @rule Props interface unchanged from legacy version — gated tools
 *       (customs/encar/auction-sheet) render this inside ToolAccessGate, not
 *       directly; the props interface stays stable.
 * @rule Telegram blue color #2AABEE — official brand color, do not customize.
 * @rule On mobile (matchMedia(coarse)) prefer tg:// scheme; desktop uses https://t.me/.
 */

'use client';

import { useState } from 'react';
import { CONTACTS } from '@/lib/constants';
import { trackGoal } from '@/lib/metrika';

// ─── TYPES ────────────────────────────────────────────────────────────────

export interface TelegramAuthBlockProps {
  usedCount: number;
  maxCount: number;
  isLimitReached: boolean;
  /** Originating tool: "auction" | "encar" | "customs" — passed to backend for source tracking */
  source: string;
  /** Called after user clicks the bot button — parent can clear local state */
  onAuthSuccess?: () => void;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────

function isMobileLike(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────

export default function TelegramAuthBlock({
  usedCount,
  maxCount,
  isLimitReached,
  source,
  onAuthSuccess,
}: TelegramAuthBlockProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (usedCount === 0 && !isLimitReached) {
    return null;
  }

  const isTeaser = !isLimitReached && usedCount === maxCount - 1;

  async function handleStartBot() {
    setLoading(true);
    setError(null);

    // Open a blank tab SYNCHRONOUSLY within the click handler. A popup opened
    // directly in response to a user gesture is not blocked; opening it AFTER
    // await fetch would be treated as programmatic and blocked. We then set its
    // location once we have the deep-link. No window.location.href on the current
    // tab — the tool page must stay intact (previous code double-opened because
    // window.open(..., 'noopener') always returns null, so the !opened fallback
    // always fired and navigated the current tab too).
    const authTab = window.open('about:blank', '_blank');

    try {
      const res = await fetch('/api/auth/bot/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      if (!res.ok) throw new Error('initiate_failed');
      const data = (await res.json()) as { webUrl: string; appUrl: string; expiresAt: number };

      trackGoal('auth_bot_deeplink_initiated');
      onAuthSuccess?.();

      const target = isMobileLike() ? data.appUrl : data.webUrl;

      if (authTab && !authTab.closed) {
        // Harden the opened tab against window.opener access, then navigate it.
        try { authTab.opener = null; } catch {}
        authTab.location.replace(target);
      } else {
        // Popup was blocked at click time (rare). Fall back to same-tab nav so
        // the user still reaches the bot.
        window.location.href = target;
      }

      // Reset loading on the success path too — without this the button stays
      // stuck on "Открываем бота..." forever (only catch reset it before). The
      // user leaves for the bot tab; on return the button is clickable again.
      setLoading(false);
    } catch (err) {
      // If we opened a blank tab but the request failed, close it to avoid a stray blank tab.
      if (authTab && !authTab.closed) {
        try { authTab.close(); } catch {}
      }
      console.error('[TelegramAuthBlock] initiate failed:', err);
      setError('Не удалось открыть бот. Напишите менеджеру или попробуйте ещё раз.');
      setLoading(false);
    }
  }

  if (isTeaser) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 my-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl" aria-hidden>⚠️</span>
          <div className="flex-1">
            <p className="text-amber-900 font-medium mb-1">
              Это последняя тестовая попытка
            </p>
            <p className="text-amber-800 text-sm mb-3">
              Для дальнейшей работы авторизуйтесь через Telegram-бота — получите 10 расшифровок / расчётов в день.
            </p>
            <button
              type="button"
              onClick={handleStartBot}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md bg-[#2AABEE] hover:bg-[#1f8fc7] active:bg-[#1a7ba8] text-white font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50"
            >
              {loading ? 'Открываем...' : 'Запустить @jckauto_help_bot'}
            </button>
            {error && (
              <p className="text-red-700 text-sm mt-2">{error}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[#2AABEE]/30 bg-blue-50 p-6 my-6">
      <div className="text-center max-w-md mx-auto">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Бесплатные попытки исчерпаны
        </h3>
        <p className="text-gray-700 mb-5">
          Запустите Telegram-бота{' '}
          <span className="font-mono text-[#2AABEE]">@jckauto_help_bot</span>{' '}
          — получите 10 расшифровок / расчётов в день бесплатно.
        </p>

        <button
          type="button"
          onClick={handleStartBot}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-[#2AABEE] hover:bg-[#1f8fc7] active:bg-[#1a7ba8] text-white font-medium px-6 py-3 text-base transition-colors disabled:opacity-50 shadow-sm w-full sm:w-auto justify-center"
        >
          {loading ? 'Открываем бота...' : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
              </svg>
              Запустить @jckauto_help_bot
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 mt-3">
          Откроется наш бот в Telegram. Без форм, без email — просто нажмите «Старт» в боте.
        </p>

        {error && (
          <p className="text-red-700 text-sm mt-4">{error}</p>
        )}

        <div className="mt-6 pt-5 border-t border-blue-200/60">
          <p className="text-sm text-gray-600">
            Не хотите бот?{' '}
            <a
              href={CONTACTS.telegram}
              className="text-[#2AABEE] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Напишите менеджеру
            </a>{' '}
            — он расшифрует вручную.
          </p>
        </div>
      </div>
    </div>
  );
}
